import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getExpiredAgreementsForPatient } from 'api/account/getExpiredAgreementsForPatient';
import { FeatureFlags, useFlag } from 'hooks/useFlag';

import { useCurrentUser } from './CurrentUserContext';

interface AgreementContextType {
  hasExpiredAgreements: boolean;
  checkAgreements: () => Promise<void>;
  setHasExpiredAgreements: (value: boolean) => void;
}

const AgreementContext = createContext<AgreementContextType | undefined>(
  undefined,
);

export const useAgreement = () => {
  const context = useContext(AgreementContext);
  if (context === undefined) {
    throw new Error('useAgreement must be used within an AgreementProvider');
  }
  return context;
};

export const AgreementProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [hasExpiredAgreements, setHasExpiredAgreements] = useState(false);
  const [hasCheckedAgreements, setHasCheckedAgreements] = useState(false);
  const { state: currentUserState } = useCurrentUser();
  const flag = useFlag(FeatureFlags.ForceRenewalAgreement);

  const checkAgreements = useCallback(async () => {
    if (!flag) return;
    if (!currentUserState.account || hasCheckedAgreements) {
      return;
    }

    for (const member of currentUserState.members ?? []) {
      if (
        !member.termsAndConditionsSignedAt &&
        member.hasPatientCompletedConsult
      ) {
        setHasExpiredAgreements(true);
        return;
      }
      const expiredAgreements = await getExpiredAgreementsForPatient(member.id);
      const agreements = expiredAgreements?.agreements ?? [];
      agreements.forEach((agreement: { refAgreementCode: string }) => {
        if (agreement.refAgreementCode === 'TERMS_AND_CONDITIONS') {
          setHasExpiredAgreements(true);
          return;
        }
      });
    }
    setHasCheckedAgreements(true);
  }, [
    currentUserState.account,
    currentUserState.members,
    flag,
    hasCheckedAgreements,
  ]);

  const resetAgreementCheck = useCallback(() => {
    setHasCheckedAgreements(false);
    return Promise.resolve();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      currentUserState.isInitialized &&
      currentUserState.account &&
      !hasCheckedAgreements
    ) {
      checkAgreements();
    }
  }, [
    currentUserState.isInitialized,
    currentUserState.account,
    checkAgreements,
    hasCheckedAgreements,
  ]);

  useEffect(() => {
    if (currentUserState.account) {
      resetAgreementCheck();
    }
  }, [currentUserState.account, resetAgreementCheck]);

  return (
    <AgreementContext.Provider
      value={{
        hasExpiredAgreements,
        checkAgreements,
        setHasExpiredAgreements,
      }}
    >
      {children}
    </AgreementContext.Provider>
  );
};
